import {
    ALGOLIA_PERSISTANT_PRODUCT_FILTERS,
    MAX_HITS_PER_PAGE,
    PRODUCT_VARIANTS_HITS_PER_PAGE,
    PRODUCT_VARIANTS_MAX_REQUEST_AMOUNT,
} from '../constants';
import type { ProductHit } from '../types';
import { AlgoliaIndice, localePrefix } from '../types';
import type { ReqOptions } from '../types/reqOptions';
import { getSearchIndex } from './getSearchIndex';
import { createAlgoliaClient } from '@libs/algolia';
import type { SearchResponse } from '@algolia/client-search';
import { logger, type Locale } from '@libs/core';

export const getObjectById = async <TObject>(id: string, indexName: string, requestOptions?: ReqOptions) => {
    try {
        const index = getSearchIndex(indexName);

        return await index.getObject<TObject>(id, requestOptions);
    } catch (error) {
        logger.error('[algolia/getObjectById]', { index: indexName }, 'error %o', error);

        return undefined;
    }
};

export const getProductById = async (id: string): Promise<ProductHit | undefined> => {
    try {
        const index = getSearchIndex(AlgoliaIndice.PRODUCTS);
        const masterIdFilter = `masterProductID:${id}`;

        const response = await index.search<ProductHit>('', {
            filters: `(${ALGOLIA_PERSISTANT_PRODUCT_FILTERS}) AND (${masterIdFilter} OR objectID:${id})`,
        });

        if (response?.hits.length > 0) {
            return response.hits[0];
        }

        return undefined;
    } catch (error) {
        console.error('[algolia/getProductById]', { index: AlgoliaIndice.PRODUCTS }, JSON.stringify(error));

        return undefined;
    }
};

export const getProductsByMasterId = async (masterId: string): Promise<ProductHit[]> => {
    if (!masterId) return [];

    try {
        const index = getSearchIndex(AlgoliaIndice.PRODUCTS_BEST_SELLERS);

        const masterProductIdFilter = `masterProductID = ${masterId}`;
        const totalHits: ProductHit[] = [];

        let currentPage = 0;
        let totalPages = 1; // Set to one, but it would be overriden by algolia's nbPages before used

        do {
            const algoliaResponse = await index.search<ProductHit>('', {
                filters: `(${ALGOLIA_PERSISTANT_PRODUCT_FILTERS}) AND (${masterProductIdFilter})`,
                distinct: 0,
                hitsPerPage: PRODUCT_VARIANTS_HITS_PER_PAGE,
                page: currentPage,
            });

            if (algoliaResponse.hits && algoliaResponse.hits.length > 0) {
                totalHits.push(...algoliaResponse.hits);
            }

            currentPage = algoliaResponse.page + 1;
            totalPages = algoliaResponse.nbPages;
        } while (currentPage < totalPages && currentPage < PRODUCT_VARIANTS_MAX_REQUEST_AMOUNT); // Hard limit to avoid infinite cycles

        return totalHits;
    } catch (error) {
        console.error('[algolia/getProductsByMasterId]', { index: AlgoliaIndice.PRODUCTS }, JSON.stringify(error));

        return [];
    }
};

export const getProductsByIds = async (ids: string[]): Promise<ProductHit[]> => {
    try {
        const idFilters = ids.map((s: string) => `objectID:${s}`).join(' OR ');
        const index = getSearchIndex(AlgoliaIndice.PRODUCTS);

        const response = await index.search<ProductHit>('', {
            filters: `(${ALGOLIA_PERSISTANT_PRODUCT_FILTERS}) AND (${idFilters})`,
            distinct: 0,
        });

        if (response.hits && response.hits.length > 0) {
            return response.hits;
        }

        return [];
    } catch (error) {
        console.error('[algolia/getProductsByIds]', { index: AlgoliaIndice.PRODUCTS }, JSON.stringify(error));

        return [];
    }
};

export const getMultipleObjectsById = async <TObject>(
    ids: string[],
    indexName: string,
    requestOptions?: ReqOptions
) => {
    try {
        const index = getSearchIndex(indexName);

        return (await index.getObjects<TObject>(ids, requestOptions)).results;
    } catch (error) {
        console.error('[algolia/getMultipleObjectsById]', { index: indexName }, JSON.stringify(error));

        return [];
    }
};

/**
 * Retrieves all product variants from Algolia search index.
 *
 * Supports two query types:
 * - **Web Style ID**: Returns all variants of a master product, with the best-selling variant first
 * - **SKU**: Returns all variants of the product, with the matching SKU variant first
 *
 * @param id - The product identifier (Web Style ID or SKU)
 * @param locale - The locale for selecting the appropriate Algolia index (en-US or en-CA)
 * @returns Array of product variants, or empty array if no matches found
 *
 * @example
 * ```typescript
 * // Query by Web Style ID with US locale
 * const variants = await getAllVariantsById('WS12345', Locale['en-US']);
 * // Returns: [bestSellerVariant, ...otherVariants]
 *
 * // Query by SKU with CA locale
 * const variants = await getAllVariantsById('SKU-ABC-123', Locale['en-CA']);
 * // Returns: [matchingSKUVariant, ...otherVariants]
 * ```
 */
export const getAllVariantsById = async (id: string, locale: Locale): Promise<ProductHit[] | undefined> => {
    const prefix = localePrefix[locale];
    const localeSpecificIndex = `r-${prefix}_products_best-sellers`;

    try {
        const client = createAlgoliaClient();

        const webStyleIdFilter = `masterProductID:${id}`;
        const skuFilter = `objectID:${id}`;

        const response = (await client.multipleQueries<ProductHit>([
            {
                indexName: localeSpecificIndex,
                query: '',
                params: {
                    filters: `(${ALGOLIA_PERSISTANT_PRODUCT_FILTERS}) AND ${webStyleIdFilter}`,
                    distinct: 0,
                    hitsPerPage: MAX_HITS_PER_PAGE,
                    attributesToRetrieve: ['*', '-inventory'],
                },
            },
            {
                indexName: localeSpecificIndex,
                query: '',
                params: {
                    filters: `(${ALGOLIA_PERSISTANT_PRODUCT_FILTERS}) AND ${skuFilter}`,
                    attributesToRetrieve: ['*', '-inventory'],
                },
            },
        ])) as {
            results: SearchResponse<ProductHit>[];
        };

        const [resultsByWebStyleId, resultsBySku] = response.results;

        if (resultsByWebStyleId.hits.length > 0) {
            return resultsByWebStyleId.hits;
        }

        if (resultsBySku.hits.length > 0 && resultsBySku.hits[0]) {
            const matchingSKUVariant = resultsBySku.hits[0];
            const matchedWebStyleIdFilter = `masterProductID:${matchingSKUVariant.masterProductID}`;

            const allOtherVariantsResponse = await client.initIndex(localeSpecificIndex).search<ProductHit>('', {
                filters: `${ALGOLIA_PERSISTANT_PRODUCT_FILTERS} AND ${matchedWebStyleIdFilter} AND NOT ${skuFilter}`,
                distinct: 0,
                hitsPerPage: MAX_HITS_PER_PAGE,
                attributesToRetrieve: ['*', '-inventory'],
            });

            return [matchingSKUVariant, ...allOtherVariantsResponse.hits];
        }

        return [];
    } catch (error) {
        console.error('[algolia/getAllVartiantsById]', { index: localeSpecificIndex, locale }, JSON.stringify(error));

        return undefined;
    }
};
